import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePageTitle } from '@/components/layout/page-title';
import { PageHeader } from '@/components/layout/PageHeader';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/features/auth/api';
import { useInvoiceSummary, useRefreshInvoices } from './api';
import { formatDate, formatMoney } from './format';
import { MonthPicker, useInvoiceMonth } from './InvoiceMonth';
import { RefreshButton } from './RefreshButton';
import { NextSyncIndicator } from './NextSyncIndicator';
import { StaleBlur, staleValue } from './StaleBlur';
import type { InvoiceCurrencyTotals, InvoiceSideSummary } from './types';

/** Direction tints: issued (income) → soft green, received (expense) → soft orange. */
type Tone = 'issued' | 'received';
const toneClass = (tone?: Tone) =>
  tone === 'issued' ? 'text-issued' : tone === 'received' ? 'text-received' : undefined;

export function SummaryPage() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const { month } = useInvoiceMonth();
  usePageTitle(t('invoices.heading.summary'));

  const query = useInvoiceSummary(month);
  const refresh = useRefreshInvoices();
  const summary = query.data;
  // Data already on screen but fetching (month switch via keepPreviousData, or a manual refresh).
  const isStale = !query.isPending && query.isFetching;
  const noCertificate = query.error instanceof ApiError && query.error.status === 400;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('invoices.heading.summary')}
        description={t('invoices.description.summary')}
        actions={
          <>
            <NextSyncIndicator />
            <MonthPicker />
            <RefreshButton {...refresh} />
          </>
        }
      />

      {/* The header stays put; the stats scroll inside their own custom-scrollbar area. The negative
          right margin parks the scrollbar in the page gutter, and pr-3 realigns the content edge. */}
      <ScrollArea className="cn-vertical-scroll -mr-3 mt-6 min-h-0 flex-1">
        <div className="pr-3">
          {query.isPending ? (
            <SummarySkeleton />
          ) : noCertificate ? (
            <Card>
              <CardContent className="py-6 text-sm text-muted-foreground">
                {t('invoices.no_certificate')}
              </CardContent>
            </Card>
          ) : query.isError ? (
            <Card>
              <CardContent className="py-6 text-sm text-destructive">
                {t('invoices.load_error')}
              </CardContent>
            </Card>
          ) : summary ? (
            <StaleBlur stale={isStale}>
              <SummaryView summary={summary} lng={lng} />
            </StaleBlur>
          ) : null}
        </div>
      </ScrollArea>
    </div>
  );
}

const ZERO_MONEY = { net: 0, gross: 0, vat: 0 };

/** Stat cards + per-currency money for the month. Empty months keep the layout, filled with zeros. */
function SummaryView({
  summary,
  lng,
}: {
  summary: { issued: InvoiceSideSummary; received: InvoiceSideSummary; totals: InvoiceCurrencyTotals[] };
  lng: string;
}) {
  const { t } = useTranslation();

  // Keep the full layout for empty months: synthesize a zero PLN block so the boxes render as 0.00
  // (PLN is the KSeF default; counts/latest already come back as 0/none).
  const totals: InvoiceCurrencyTotals[] = summary.totals.length
    ? summary.totals
    : [{ currency: 'PLN', issued: ZERO_MONEY, received: ZERO_MONEY, vatBalance: 0 }];

  // Dominant currency first; its VAT balance rides in the top row next to the counts. Extra
  // currencies (rare) get their own grouped block, balance included.
  const [primary, ...rest] = totals;
  const multiCurrency = totals.length > 1;

  return (
    <div className="space-y-4">
      {/* Row 1: issued count, received count, VAT balance (dominant currency). */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <SideCard label={t('invoices.subject.SELLER')} side={summary.issued} lng={lng} tone="issued" />
        <SideCard label={t('invoices.subject.BUYER')} side={summary.received} lng={lng} tone="received" />
        {primary ? (
          <StatCard
            label={t('invoices.summary.vat_balance')}
            value={formatMoney(lng, primary.vatBalance, primary.currency)}
            hint={multiCurrency ? primary.currency : t('invoices.summary.vat_balance_short')}
          />
        ) : null}
      </div>

      {/* Rows 2 & 3: issued then received money (net, tax, gross) for the dominant currency. */}
      {primary ? (
        <CurrencyMoney totals={primary} lng={lng} showCurrency={multiCurrency} showBalance={false} />
      ) : null}

      {/* Any further currencies, each fully grouped (their own balance). */}
      {rest.map((totals) => (
        <CurrencyMoney key={totals.currency} totals={totals} lng={lng} showCurrency showBalance />
      ))}
    </div>
  );
}

/** Count headline for one side, with the latest invoice (number + date) as the hint. */
function SideCard({
  label,
  side,
  lng,
  tone,
}: {
  label: string;
  side: InvoiceSideSummary;
  lng: string;
  tone: Tone;
}) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className={cn('text-2xl tabular-nums', toneClass(tone), staleValue)}>
          {side.count}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {side.latest ? (
          <p className="text-xs text-muted-foreground">
            {t('invoices.summary.latest')}:{' '}
            <Link
              to={`/invoices/${tone}/${encodeURIComponent(side.latest.ksefNumber)}`}
              className={cn(
                'font-medium text-foreground underline-offset-4 hover:underline',
                'focus-visible:rounded-xs focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                staleValue,
              )}
            >
              {side.latest.invoiceNumber ?? side.latest.ksefNumber}
            </Link>
            {side.latest.issueDate ? (
              <span className={staleValue}> · {formatDate(lng, side.latest.issueDate)}</span>
            ) : null}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            <span className={staleValue}>{t('invoices.summary.none')}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Money for one currency as stat boxes: one row of issued (net, tax, gross), one row of received.
 * `showBalance` adds the currency's VAT-balance box (used for non-dominant currencies, whose balance
 * isn't already in the summary's top row).
 */
function CurrencyMoney({
  totals,
  lng,
  showCurrency,
  showBalance,
}: {
  totals: InvoiceCurrencyTotals;
  lng: string;
  showCurrency: boolean;
  showBalance: boolean;
}) {
  const { t } = useTranslation();
  const m = (amount: number) => formatMoney(lng, amount, totals.currency);
  const issued = t('invoices.subject.SELLER');
  const received = t('invoices.subject.BUYER');
  return (
    <div className="space-y-4">
      {showCurrency ? (
        <h2 className="text-sm font-medium text-muted-foreground">{totals.currency}</h2>
      ) : null}
      {/* Issued (income): net, tax, gross. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard label={t('invoices.stats.net')} value={m(totals.issued.net)} hint={issued} tone="issued" />
        <StatCard label={t('invoices.stats.vat')} value={m(totals.issued.vat)} hint={issued} tone="issued" />
        <StatCard label={t('invoices.stats.gross')} value={m(totals.issued.gross)} hint={issued} tone="issued" />
      </div>
      {/* Received (expense): net, tax, gross. */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard label={t('invoices.stats.net')} value={m(totals.received.net)} hint={received} tone="received" />
        <StatCard label={t('invoices.stats.vat')} value={m(totals.received.vat)} hint={received} tone="received" />
        <StatCard label={t('invoices.stats.gross')} value={m(totals.received.gross)} hint={received} tone="received" />
      </div>
      {showBalance ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            label={t('invoices.summary.vat_balance')}
            value={m(totals.vatBalance)}
            hint={t('invoices.summary.vat_balance_short')}
          />
        </div>
      ) : null}
    </div>
  );
}

/** A plain stat box: label, big value, small hint — the shape shared by every summary box. */
function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  tone?: Tone;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{label}</CardDescription>
        <CardTitle className={cn('text-2xl tabular-nums', toneClass(tone), staleValue)}>{value}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

function SummarySkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    </div>
  );
}
